// Les attestations de fin de formation d'une formation.
//
// **Elle certifie une durée, donc elle se refuse quand la durée n'est pas
// établie.** C'est ce que cet écran montre d'abord : qui est émargeable, qui
// ne l'est pas, et quelles séances manquent. Un bouton « Éditer » qui ne
// dirait pas pourquoi il est gris enverrait chercher la cause ailleurs.
//
// Le signataire signe **une fois pour la formation** : un tracé par stagiaire
// serait douze copies de la même image pour un geste unique.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Award, Loader2, X, FileDown, Mail, Ban, AlertTriangle, CheckCircle2, Pen,
} from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';
import { messageErreur } from '../lib/erreur';
import SignaturePad from './SignaturePad';

const dateHeure = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

export default function PanneauAttestations({ feuille, onFermer, onChange }) {
  const sessionId = feuille.session_id;
  const [etat, setEtat] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [occupe, setOccupe] = useState(null);
  const [emission, setEmission] = useState(false);
  const [nom, setNom] = useState('');
  const [qualite, setQualite] = useState('');
  // Vide a dessein : le serveur compose alors la phrase **de chaque
  // apprenant** — le resultat de son quiz, le verdict de sa grille. Une phrase
  // pre-remplie ici l'emporte sur le calcul (`payload.resultats or
  // phrase_resultats(r)`), donc les douze attestations d'une formation
  // porteraient la meme ligne, celle que personne n'a ecrite.
  const [resultats, setResultats] = useState('');

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await api.get(`/sessions/${sessionId}/attestations`);
      setEtat(data);
    } catch (err) {
      toast({ title: 'Erreur', description: messageErreur(err, 'Lecture impossible.'),
              variant: 'destructive' });
    } finally { setChargement(false); }
  }, [sessionId]);

  useEffect(() => { charger(); }, [charger]);

  // Émettre : le tracé du signataire part avec, et c'est le même pour toute la
  // formation.
  const emettre = async (traceB64, nomSigne) => {
    setOccupe('emission');
    try {
      const { data } = await api.post(`/sessions/${sessionId}/attestations`, {
        signataire_nom: (nomSigne || nom || '').trim(),
        signataire_qualite: qualite.trim() || null,
        signature_b64: traceB64 || null,
        resultats: resultats.trim() || null,
      });
      const n = (data.emises || []).length;
      const r = (data.refusees || []).length;
      toast({
        title: n ? `${n} attestation${n > 1 ? 's' : ''} éditée${n > 1 ? 's' : ''}` : 'Aucune attestation éditée',
        description: r ? `${r} refusée${r > 1 ? 's' : ''} — émargement incomplet.` : undefined,
        variant: n ? undefined : 'destructive',
      });
      setEmission(false);
      await charger();
      onChange && onChange();
    } catch (err) {
      toast({ title: 'Édition impossible', description: messageErreur(err),
              variant: 'destructive' });
    } finally { setOccupe(null); }
  };

  const ouvrir = async (id) => {
    setOccupe(id);
    try {
      const res = await api.get(`/attestations/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast({ title: 'Impossible', description: messageErreur(err), variant: 'destructive' });
    } finally { setOccupe(null); }
  };

  const envoyer = async () => {
    setOccupe('envoi');
    try {
      const { data } = await api.post(`/sessions/${sessionId}/attestations/envoyer`);
      const n = (data.envoyees || []).length;
      const sans = (data.sans_adresse || []).length;
      const ko = (data.echecs || []).length;
      toast({
        title: n ? `${n} attestation${n > 1 ? 's' : ''} envoyée${n > 1 ? 's' : ''}` : 'Aucun envoi',
        description: [
          sans ? `${sans} sans adresse` : null,
          ko ? `${ko} en échec` : null,
        ].filter(Boolean).join(' · ') || 'Chaque apprenant a reçu la sienne.',
        variant: n ? undefined : 'destructive',
      });
      await charger();
    } catch (err) {
      toast({ title: 'Envoi impossible', description: messageErreur(err),
              variant: 'destructive' });
    } finally { setOccupe(null); }
  };

  const annuler = async (id, numero) => {
    const motif = window.prompt(
      `Annuler l'attestation ${numero} ? Dites pourquoi — c'est la seule question qu'on se posera dans six mois.`);
    if (!motif || !motif.trim()) return;
    setOccupe(id);
    try {
      await api.post(`/attestations/${id}/annuler`, { motif: motif.trim() });
      toast({ title: 'Attestation annulée', description: numero });
      await charger();
      onChange && onChange();
    } catch (err) {
      toast({ title: 'Impossible', description: messageErreur(err), variant: 'destructive' });
    } finally { setOccupe(null); }
  };

  const items = etat?.items || [];

  return (
    <div className="soft-card p-5" data-testid={`panneau-attestations-${sessionId}`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#EEF2FF] flex items-center justify-center shrink-0">
          <Award className="w-4 h-4 text-[#4F46E5]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold text-[#1A1B2E]"
               style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Attestations de fin de formation
          </div>
          <div className="text-[12px] text-[#8E8FA5] truncate">
            {feuille.titre || sessionId}
            {etat && ` · ${etat.emises} éditée(s) · ${etat.emettables} à éditer · ${etat.bloquees} bloquée(s)`}
          </div>
        </div>
        <button onClick={onFermer} data-testid="fermer-attestations"
                title="Fermer" aria-label="Fermer"
                className="w-8 h-8 rounded-lg hover:bg-[#F6F7FB] text-[#6B6C85] flex items-center justify-center">
          <X className="w-4 h-4" />
        </button>
      </div>

      {chargement && (
        <div className="py-6 text-center text-[13px] text-[#8E8FA5]">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…
        </div>
      )}

      {!chargement && items.length === 0 && (
        <div className="py-6 text-center text-[13px] text-[#8E8FA5]">
          Aucun apprenant sur cette formation — ni inscription, ni ligne d'émargement.
        </div>
      )}

      {!chargement && items.length > 0 && (
        <div className="mt-3 divide-y divide-[#F3F4F8]">
          {items.map((it) => (
            <div key={it.apprenant_id} className="py-2.5 flex items-center gap-2"
                 data-testid={`attestation-ligne-${it.apprenant_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">
                  {it.nom}
                  {/* Deux documents peuvent coexister : l'attestation de fin de
                      formation, due dans tous les cas, et celle de validation
                      des acquis. Les nommer pareil obligerait à les ouvrir. */}
                  {(it.documents || []).map((d) => (
                    <span key={d.id}
                          title={d.titre}
                          className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                            d.type_attestation === 'VALIDATION_ACQUIS'
                              ? 'bg-[#EEF2FF] text-[#4F46E5]'
                              : 'bg-[#E6FBF7] text-[#0F766E]'
                          }`}>
                      <CheckCircle2 className="w-2.5 h-2.5" />
                      {d.type_attestation === 'VALIDATION_ACQUIS' ? 'Acquis · ' : ''}
                      {d.numero}
                    </span>
                  ))}
                </div>
                <div className="text-[11px] text-[#8E8FA5] truncate">
                  {it.assiduite
                    ? `${it.assiduite.duree_suivie_texte} suivies sur ${it.assiduite.duree_prevue_texte} · `
                      + `${it.assiduite.seances_suivies}/${it.assiduite.seances_total} séance(s)`
                    : 'aucune ligne d’émargement'}
                  {it.email ? ` · ${it.email}` : ' · aucune adresse'}
                </div>
                {it.refus && (
                  <div className="text-[10.5px] font-semibold text-[#B45309]">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />{it.refus}
                  </div>
                )}
                {it.attestation?.envoyee_le && (
                  <div className="text-[10.5px] text-[#0F766E]">
                    envoyée le {dateHeure(it.attestation.envoyee_le)} à {it.attestation.envoyee_a}
                  </div>
                )}
                {/* Ce que l'attestation écrit noir sur blanc : la mention que
                    la loi impose, et qui était tapée à la main. */}
                {(it.documents || [])[0]?.resultats && (
                  <div className="text-[10.5px] text-[#6B6C85] truncate"
                       title={it.documents[0].resultats}>
                    {it.documents[0].resultats}
                  </div>
                )}
              </div>
              {(it.documents || []).map((d) => (
                <React.Fragment key={d.id}>
                  <button onClick={() => ouvrir(d.id)} disabled={!!occupe}
                          data-testid={`ouvrir-attestation-${d.type_attestation}-${it.apprenant_id}`}
                          title={`Ouvrir — ${d.titre}`} aria-label={`Ouvrir ${d.titre}`}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-50 ${
                            d.type_attestation === 'VALIDATION_ACQUIS'
                              ? 'hover:bg-[#EEF2FF] text-[#4F46E5]'
                              : 'hover:bg-[#E6FBF7] text-[#0F766E]'
                          }`}>
                    {occupe === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                     : <FileDown className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={() => annuler(d.id, d.numero)}
                          disabled={!!occupe}
                          data-testid={`annuler-attestation-${d.type_attestation}-${it.apprenant_id}`}
                          title={`Annuler — ${d.titre}`} aria-label={`Annuler ${d.titre}`}
                          className="w-8 h-8 rounded-lg hover:bg-[#FEF6F6] text-[#B91C1C] flex items-center justify-center disabled:opacity-50">
                    <Ban className="w-3.5 h-3.5" />
                  </button>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      )}

      {!chargement && (etat?.annulees || []).length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#EFF0F6] text-[11px] text-[#8E8FA5]">
          <div className="font-semibold text-[#6B6C85] mb-1">Annulées</div>
          {etat.annulees.map((a) => (
            <div key={a.id} className="truncate">
              {a.numero} · {a.nom} · le {dateHeure(a.annulee_le)} — {a.motif}
            </div>
          ))}
        </div>
      )}

      {/* L'émission : le signataire, sa qualité, les résultats, puis le tracé. */}
      {!chargement && (
        <div className="mt-4 pt-4 border-t border-[#EFF0F6]">
          {!emission ? (
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setEmission(true)}
                disabled={!etat?.emettables || !!occupe}
                data-testid={`editer-attestations-${sessionId}`}
                title={etat?.emettables
                  ? `Éditer ${etat.emettables} attestation(s)`
                  : "Aucun apprenant n'est émargé en entier — l'attestation certifie une durée"}
                className="h-9 px-4 rounded-lg bg-[#4F46E5] text-white text-[13px] font-semibold flex items-center gap-1.5 hover:opacity-95 disabled:opacity-50"
              >
                <Pen className="w-3.5 h-3.5" />
                Éditer les attestations{etat?.emettables ? ` (${etat.emettables})` : ''}
              </button>
              {etat?.emises > 0 && (
                <button onClick={envoyer} disabled={!!occupe}
                        data-testid={`envoyer-attestations-${sessionId}`}
                        className="h-9 px-4 rounded-lg bg-white border border-[#C7D2FE] text-[13px] font-semibold text-[#4F46E5] flex items-center gap-1.5 hover:bg-[#EEF2FF] disabled:opacity-50">
                  {occupe === 'envoi' ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                      : <Mail className="w-3.5 h-3.5" />}
                  Envoyer à chaque apprenant
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3" data-testid="formulaire-emission">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[12px] font-semibold text-[#4A4B63]">Signataire</span>
                  <input value={nom} onChange={(e) => setNom(e.target.value)}
                         data-testid="attestation-signataire"
                         placeholder="Nom et prénom"
                         className="mt-1 w-full h-9 px-3 rounded-lg border border-[#EFF0F6] text-[13px]" />
                </label>
                <label className="block">
                  <span className="text-[12px] font-semibold text-[#4A4B63]">Qualité</span>
                  <input value={qualite} onChange={(e) => setQualite(e.target.value)}
                         data-testid="attestation-qualite"
                         placeholder="Responsable pédagogique, gérant…"
                         className="mt-1 w-full h-9 px-3 rounded-lg border border-[#EFF0F6] text-[13px]" />
                </label>
              </div>
              <label className="block">
                <span className="text-[12px] font-semibold text-[#4A4B63]">
                  Résultats de l'évaluation des acquis
                </span>
                <textarea value={resultats} onChange={(e) => setResultats(e.target.value)}
                          rows={2} data-testid="attestation-resultats"
                          className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EFF0F6] text-[13px]" />
                <span className="text-[11px] text-[#8E8FA5]">
                  La loi demande qu'ils figurent sur l'attestation.
                  <strong> Laissé vide</strong>, chaque attestation porte le résultat de
                  son apprenant — sa note au quiz, le verdict de sa grille. Ce qu'on
                  écrit ici l'emporte, et vaut pour les {etat?.emettables || 0}{' '}
                  attestation(s) éditées maintenant.
                </span>
              </label>
              <div className="rounded-lg border border-[#EFF0F6] p-3">
                <div className="text-[12px] font-semibold text-[#4A4B63] mb-2">
                  Votre signature — une seule fois pour toute la formation
                </div>
                <SignaturePad initialName={nom} demanderLeNom={false}
                              onSave={(trace, n) => emettre(trace, n)}
                              onCancel={() => setEmission(false)} />
              </div>
              {occupe === 'emission' && (
                <div className="text-[12px] text-[#4F46E5] flex items-center gap-1">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Édition…
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
